import { useImperativeHandle, useMemo, useState, forwardRef } from "react";
import type { ReactNode } from "react";
import { Button, Checkbox, Dialog, InputNumber, Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";
import type { ColumnProps } from "@/views/components/ProTable/interface";

export interface ColSettingRef {
    openColSetting: (list: ColumnProps[]) => void;
}

interface ColSettingProps {
    originTableColumns?: ColumnProps[];
    onSubmit?: (list: ColumnProps[]) => void;
}

/** 自定义列弹窗，保留 Vue 版 openColSetting/submit 的调用契约。 */
const ColSetting = forwardRef<ColSettingRef, ColSettingProps>(function ColSetting(
    { originTableColumns = [], onSubmit },
    ref
) {
    const [visible, setVisible] = useState(false);
    const [checked, setChecked] = useState(false);
    const [columns, setColumns] = useState<ColumnProps[]>([]);

    useImperativeHandle(ref, () => ({
        openColSetting(list) {
            setColumns(list);
            setVisible(true);
        }
    }));

    const updateColumn = (prop: string, patch: Partial<ColumnProps>) => {
        setColumns(current => current.map(item => (String(item.prop) === prop ? { ...item, ...patch } : item)));
    };

    const tableColumns = useMemo<TableColumnProps<ColumnProps>[]>(
        () => [
            {
                label: "选择",
                width: 64,
                render: ({ row }) => (
                    <Checkbox
                        checked={Boolean(row.isShow)}
                        disabled={Boolean(row.readonly || row.isCustomDisable)}
                        onChange={event => updateColumn(String(row.prop), { isShow: event.target.checked })}
                    />
                )
            },
            {
                label: "名称",
                prop: "label",
                render: ({ row }) => (
                    <span>
                        {row.label as ReactNode}
                        {row.readonly ? <span className="ml-10">（必选）</span> : null}
                    </span>
                )
            },
            {
                label: "宽度",
                render: ({ row }) => (
                    <InputNumber
                        min={10}
                        max={1000}
                        value={Number(row.width ?? row.minWidth ?? 120)}
                        disabled={Boolean(row.readonly || row.isCustomDisable)}
                        onChange={value => updateColumn(String(row.prop), { width: Number(value) })}
                    />
                )
            }
        ],
        [columns]
    );

    const reset = () => {
        const origin = originTableColumns.map(item => ({ ...item }));
        setColumns(current =>
            checked
                ? origin
                : current.map(item => {
                      if (!item.isShow || item.prop === "operation") return item;
                      return origin.find(originItem => originItem.prop === item.prop) ?? item;
                  })
        );
    };

    const submit = () => {
        onSubmit?.(columns);
        setVisible(false);
    };

    return (
        <Dialog
            open={visible}
            title="自定义列"
            width={687}
            onOpenChange={setVisible}
            footer={
                <div className="dialog-footer">
                    <Checkbox checked={checked} onChange={event => setChecked(event.target.checked)}>
                        全局
                    </Checkbox>
                    <div className="flex-1">
                        <Button onClick={() => setVisible(false)}>取消</Button>
                        <Button onClick={reset}>{checked ? "全局恢复默认" : "恢复默认"}</Button>
                        <Button type="primary" onClick={submit}>
                            保存
                        </Button>
                    </div>
                </div>
            }
        >
            <ZhTable<ColumnProps> data={columns} columns={tableColumns} rowKey={row => String(row.prop)} />
        </Dialog>
    );
});

export default ColSetting;
